/**
 * Collapsible "New payment intent" form. Self-gates on
 * `payment.create_intent`.
 *
 * A payment intent is a request for an inbound payment against a loan, so
 * the form leads with a lazy loan picker (`enabled: open`). The loan rows
 * only carry a `customerId`, so the customers list is fetched alongside
 * (shared cache key, deduped) to label each loan by its customer — name +
 * masked phone — instead of an opaque id slice. Amount is in
 * minor units and must be positive (the DTO uses `.positive()`, unlike
 * the device/loan value fields which allow zero). Reference is optional —
 * the service generates one when omitted. TTL defaults to 60 minutes.
 *
 * On success the payment-intents list query is invalidated.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { CreatePaymentIntentRequest, Customer, Loan, PaymentIntent } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { useCountryConfig } from "../lib/useCountryConfig.js";

const MAX_TTL_MINUTES = 60 * 24;

export function CreatePaymentIntentForm(): JSX.Element | null {
  const auth = useAuth();
  const canCreate = (auth.user?.permissions ?? []).includes("payment.create_intent");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const loans = useQuery({
    queryKey: ["loans", "list"],
    queryFn: () => api.get<readonly Loan[]>("/api/loans"),
    enabled: open,
  });
  const customers = useQuery({
    queryKey: ["customers", "list"],
    queryFn: () => api.get<readonly Customer[]>("/api/customers"),
    enabled: open,
  });
  const cfg = useCountryConfig(open);

  const customerById = new Map((customers.data ?? []).map((c) => [c.id, c]));

  /** Identify a loan by its customer (name + masked phone), falling back to an id slice. */
  function loanLabel(loan: Loan): string {
    const c = customerById.get(loan.customerId);
    const who = c ? `${c.givenName} ${c.familyName} · ${c.phoneMasked}` : `${loan.id.slice(0, 8)}…`;
    return `${who} · ${loan.status}`;
  }

  const [loanId, setLoanId] = useState("");
  const [amountMinor, setAmountMinor] = useState("");
  const [currency, setCurrency] = useState("");
  const [channel, setChannel] = useState("");
  const [reference, setReference] = useState("");
  const [ttlMinutes, setTtlMinutes] = useState("60");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreatePaymentIntentRequest) =>
      api.post<PaymentIntent>("/api/payment-intents", body),
    onSuccess: () => {
      reset();
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["payment-intents", "list"] });
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setLoanId("");
    setAmountMinor("");
    setCurrency("");
    setChannel("");
    setReference("");
    setTtlMinutes("60");
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (!loanId) {
      setError("Loan is required.");
      return;
    }
    const amount = Number(amountMinor);
    if (!Number.isInteger(amount) || amount <= 0) {
      setError("Amount must be a positive whole number (minor units).");
      return;
    }
    const cur = currency.trim().toUpperCase();
    if (cur.length !== 3) {
      setError("Currency must be a 3-letter code (e.g. TZS).");
      return;
    }
    if (channel.trim().length < 1) {
      setError("Channel is required.");
      return;
    }
    const ttl = Number(ttlMinutes);
    if (!Number.isInteger(ttl) || ttl <= 0 || ttl > MAX_TTL_MINUTES) {
      setError(`TTL must be a whole number of minutes between 1 and ${MAX_TTL_MINUTES}.`);
      return;
    }
    const ref = reference.trim();
    mutation.mutate({
      loanId,
      amount: { amountMinor: amount, currency: cur },
      channel: channel.trim(),
      ttlMinutes: ttl,
      ...(ref.length > 0 ? { reference: ref } : {}),
    });
  }

  if (!canCreate) return null;

  if (!open) {
    return <Button onClick={() => setOpen(true)}>New payment intent…</Button>;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">New payment intent</p>

      <Field label="Loan">
        <Select value={loanId} onChange={(e) => setLoanId(e.target.value)}>
          <option value="">Select loan…</option>
          {(loans.data ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {loanLabel(l)}
            </option>
          ))}
        </Select>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount" hint="minor units">
          <TextInput
            inputMode="numeric"
            value={amountMinor}
            onChange={(e) => setAmountMinor(e.target.value)}
          />
        </Field>
        <Field label="Currency">
          <Select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={!cfg.data}
          >
            <option value="">{cfg.data ? "Select currency…" : "Loading…"}</option>
            {cfg.data ? <option value={cfg.data.currency}>{cfg.data.currency}</option> : null}
          </Select>
        </Field>
        <Field label="Channel">
          <Select value={channel} onChange={(e) => setChannel(e.target.value)} disabled={!cfg.data}>
            <option value="">{cfg.data ? "Select channel…" : "Loading…"}</option>
            {(cfg.data?.paymentProviders ?? []).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="TTL" hint="minutes">
          <TextInput
            inputMode="numeric"
            value={ttlMinutes}
            onChange={(e) => setTtlMinutes(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Reference" hint="optional; auto-generated if blank">
        <TextInput value={reference} onChange={(e) => setReference(e.target.value)} />
      </Field>

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Create intent"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
